import AllTransactions from './AllTransactions';
import AllBalances from './AllBalances';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { letter as originalLetter } from './letter';
import { promiseLetter } from './promiseLetter';
import LettersIndex from './LettersIndex';
import HealingBackdrop from './HealingBackdrop';
import Construction from './Construction';
import BankHome from './BankHome';
import Register from './Register';
import UserManagement from './UserManagement';
import Vault from './Vault';
import Universe from './Universe';
import Chimichangas from './Chimichangas';
import { MagicBackdrop, MagicIcon, StoryWorlds } from './Magic';

const tokens = text => text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean).map(value => {
  const strong = value.startsWith('**') && value.endsWith('**');
  const emphasis = !strong && value.startsWith('*') && value.endsWith('*');
  return { type: strong ? 'strong' : emphasis ? 'em' : 'plain', text: strong ? value.slice(2, -2) : emphasis ? value.slice(1, -1) : value };
});
function prepareBlocks(letter) {
let chapterWorld = 'magic';
return letter.blocks.map(block => {
  if (block.type === 'heading') {
    chapterWorld = /merlina/i.test(block.text) ? 'merlina' : /shrek/i.test(block.text) ? 'shrek' : 'magic';
  }
  return { ...block, world: chapterWorld, length: tokens(block.text).reduce((sum, token) => sum + Array.from(token.text).length, 0) };
});
}

function FormattedText({ text, limit = Infinity, glow = false }) {
  let remaining = limit;
  return tokens(text).map((token, index) => {
    const chars = Array.from(token.text);
    const visibleCount = Math.max(0, Math.min(chars.length, remaining));
    const trailStart = Math.max(0, Math.min(visibleCount, remaining - 28));
    const visible = glow ? <><span key="settled">{chars.slice(0, trailStart).join('')}</span>{chars.slice(trailStart, visibleCount).map((character, characterIndex) => <span className="ink-glow" key={trailStart + characterIndex}>{character}</span>)}</> : chars.slice(0, visibleCount).join('');
    remaining -= chars.length;
    if (token.type === 'strong') return <strong key={index}>{visible}</strong>;
    if (token.type === 'em') return <em key={index}>{visible}</em>;
    return <span key={index}>{visible}</span>;
  });
}

const LetterBlock = memo(function LetterBlock({ block, visible = block.length, cursor = false }) {
  if (block.type === 'divider') return <div className={`letter-divider world-${block.world}`} aria-hidden="true"><MagicIcon name={block.world === 'merlina' ? 'rose' : block.world === 'shrek' ? 'crown' : 'snitch'} /></div>;
  const Tag = block.type === 'heading' ? 'h3' : 'p';
  const className = block.type === 'heading' && block.level === 1 ? 'letter-question' : undefined;
  return <>{block.type === 'heading' && /harry potter|merlina|shrek/i.test(block.text) && <div className={`chapter-emblem world-${block.world}`} aria-hidden="true"><MagicIcon name={block.world === 'merlina' ? 'hand' : block.world === 'shrek' ? 'ogre' : 'owl'} /></div>}<Tag className={className}><span className="invisible-copy"><FormattedText text={block.text} /></span><span className="written-copy"><FormattedText text={block.text} limit={visible} glow={cursor} />{cursor && <span className="ink-cursor" aria-hidden="true" />}</span></Tag></>;
});

const AccessibleLetter = memo(function AccessibleLetter({ blocks }) {
  return <div className="sr-only">{blocks.map((block, index) => block.type === 'divider' ? <hr key={index} /> : block.type === 'heading' ? <h3 key={index}><FormattedText text={block.text} /></h3> : <p key={index}><FormattedText text={block.text} /></p>)}</div>;
});

function useSmoothPaper(paperRef, contentRef, opened, complete, reduced) {
  const sealedHeight = useRef(0);
  useEffect(() => {
    const paper = paperRef.current;
    const content = contentRef.current;
    if (!paper || !content) return;
    let frame = 0;
    let previousTime = 0;
    let height = paper.getBoundingClientRect().height;
    let target = height;
    const animate = time => {
      const elapsed = previousTime ? Math.min(40, time - previousTime) : 16;
      previousTime = time;
      const difference = target - height;
      const step = Math.sign(difference) * Math.min(Math.abs(difference) * (1 - Math.exp(-elapsed / 160)), elapsed * .32);
      height += step;
      paper.style.height = `${height}px`;
      if (Math.abs(target - height) > .3) frame = requestAnimationFrame(animate);
      else { paper.style.height = `${target}px`; frame = 0; previousTime = 0; }
    };
    const measure = () => {
      const measured = content.getBoundingClientRect().height;
      if (!opened) sealedHeight.current = measured;
      if (!opened || reduced || complete) {
        paper.style.height = '';
        return;
      }
      target = Math.max(sealedHeight.current, measured);
      if (!frame) frame = requestAnimationFrame(animate);
    };
    // Start opening/replaying at the closed scroll's height, without a collapse.
    if (opened && !complete && !reduced) {
      height = sealedHeight.current || height;
      paper.style.height = `${height}px`;
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [paperRef, contentRef, opened, complete, reduced]);
}

function Ornament({ bottom = false }) {
  return <svg className={`ornament ${bottom ? 'ornament-bottom' : ''}`} viewBox="0 0 300 62" fill="none" aria-hidden="true">
    <path d="M150 55C127 21 77 44 49 22C33 9 17 18 23 30C28 39 40 33 36 26M150 55C173 21 223 44 251 22C267 9 283 18 277 30C272 39 260 33 264 26M150 47C128 11 103 6 92 17C81 29 104 37 110 23M150 47C172 11 197 6 208 17C219 29 196 37 190 23M150 39C132 22 143 9 150 4C157 9 168 22 150 39M60 36C67 47 83 51 95 45M240 36C233 47 217 51 205 45" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M48 22C44 10 34 5 28 8C29 18 37 22 48 22M252 22C256 10 266 5 272 8C271 18 263 22 252 22M119 32C113 17 120 11 126 10C133 20 129 28 119 32M181 32C187 17 180 11 174 10C167 20 171 28 181 32" fill="currentColor" opacity=".7"/>
    <path d="M8 49H104M196 49H292" stroke="currentColor" opacity=".45"/><path d="m150 47 5 7-5 7-5-7z" fill="currentColor"/>
  </svg>;
}



function LetterPage({ letter = originalLetter }) {
  const blocks = useMemo(()=>prepareBlocks(letter),[letter]);
  const healing = letter.theme === 'healing';
  const [opened, setOpened] = useState(false);
  const [count, setCount] = useState(0);
  const [reduced, setReduced] = useState(false);
  const [lumos, setLumos] = useState(false);
  const paperRef = useRef(null);
  const paperSurfaceRef = useRef(null);
  const paperContentRef = useRef(null);
  const progressRef = useRef(null);
  const total = blocks.reduce((sum, block) => sum + block.length, 0);
  const complete = count >= total;
  useSmoothPaper(paperSurfaceRef, paperContentRef, opened, complete, reduced);

  useEffect(() => {
    document.title = `EscarbancoCHP · ${letter.title}`;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(media.matches);
    update(); media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!opened) return;
    if (reduced) { setCount(total); return; }
    if (complete) return;
    let frame;
    let previousTime;
    let pending = 0;
    const write = time => {
      if (previousTime !== undefined) {
        // A background tab must not jump ahead when it becomes visible again.
        pending += Math.min(time - previousTime, 40) * .085;
        const characters = Math.floor(pending);
        if (characters > 0) {
          pending -= characters;
          setCount(value => Math.min(total, value + characters));
        }
      }
      previousTime = time;
      frame = requestAnimationFrame(write);
    };
    frame = requestAnimationFrame(write);
    return () => cancelAnimationFrame(frame);
  }, [opened, reduced, complete, total]);

  useEffect(() => {
    let frame = 0;
    let previousTime = 0;
    let edgeFlex = 0;
    let rollRotation = 0;
    const update = time => {
      const elapsed = previousTime ? Math.min(40, time - previousTime) : 16;
      previousTime = time;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const fraction = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${fraction})`;
      // Use scroll distance: adding text must not change the rotation target.
      const targetFlex = reduced ? 0 : Math.sin(window.scrollY / 220) * 1.2;
      const targetRoll = reduced ? 0 : window.scrollY * .04;
      const easing = reduced ? 1 : 1 - Math.exp(-elapsed / 180);
      edgeFlex += (targetFlex - edgeFlex) * easing;
      rollRotation += (targetRoll - rollRotation) * easing;
      if (paperRef.current) {
        paperRef.current.style.setProperty('--edge-flex', `${edgeFlex}px`);
        paperRef.current.style.setProperty('--edge-light', `${rollRotation * 7.5}px`);
        paperRef.current.style.setProperty('--roll-rotation', `${rollRotation}deg`);
      }
      if (Math.abs(targetFlex - edgeFlex) > .001 || Math.abs(targetRoll - rollRotation) > .01) frame = requestAnimationFrame(update);
      else { frame = 0; previousTime = 0; }
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    schedule(); window.addEventListener('scroll', schedule, { passive: true }); window.addEventListener('resize', schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    return () => { observer.disconnect(); window.cancelAnimationFrame(frame); window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); };
  }, [reduced, opened]);

  const reveal = () => { setOpened(true); if (reduced) setCount(total); };
  let offset = 0;
  return <div className={`letter-page ${lumos ? 'lumos-on' : ''}`}>
    {healing ? <HealingBackdrop /> : <MagicBackdrop mixed />}
    <header className="site-header"><Link className="brand" to="/" aria-label="EscarbancoCHP, inicio">EscarbancoCHP<span>✦</span></Link><button className="lumos-button" onClick={() => setLumos(value => !value)} aria-pressed={lumos} aria-label={lumos ? 'Nox: apagar la luz mágica' : 'Lumos: encender la luz mágica'}><MagicIcon name="wand" /><span>{lumos ? 'Nox' : 'Lumos'}</span></button></header>
    <main>
      <Link className="letters-back-link" to="/Escritos-para-un-amor-bonito">← Todos nuestros escritos</Link><section className="intro">{healing ? <div className="story-worlds healing-worlds" aria-label="Un corazón y un amor que está sanando"><div className="story-world world-magic"><MagicIcon name="heart"/><span>Nuestro amor</span><small>TE ELIJO</small></div><span className="world-connector" aria-hidden="true">✧</span><div className="story-world world-healing"><MagicIcon name="healing-heart"/><span>Sanando juntos</span><small>UN DÍA A LA VEZ</small></div></div> : <StoryWorlds />}<span className="eyebrow">{healing ? 'UN AMOR QUE APRENDE A CUIDARSE.' : 'TRES MUNDOS. UNA HISTORIA NUESTRA.'}</span><h1>{letter.introHeading || 'Para ti,'}<br /><em>{letter.introEmphasis || `${letter.recipient}.`}</em></h1><p>{letter.introCopy || <>Un poquito de magia, un amor por la eternidad<br />y un cuento en el que podemos ser nosotros.</>}</p><div className="intro-flourish" aria-hidden="true"><span /><MagicIcon name={healing ? 'healing-heart' : 'snitch'} /><span /></div></section>
      <div className="scroll-stage">
        <article className={`parchment ${opened ? 'is-open' : ''}`} ref={paperRef} aria-label={`Carta para ${letter.recipient}`}>
          <div className="scroll-roll roll-top" aria-hidden="true"><span /></div>
          <div className="paper" ref={paperSurfaceRef}><div className="paper-edge edge-left" aria-hidden="true" /><div className="paper-edge edge-right" aria-hidden="true" /><div className="paper-border" aria-hidden="true" /><div className="paper-content" ref={paperContentRef}><Ornament />
            <div className="paper-heading"><span className="eyebrow">PARA GUARDAR CERQUITA</span><h2>{letter.title}</h2><div className="paper-world-marks" aria-hidden="true">{healing ? <><MagicIcon name="heart" className="paper-magic-mark"/><MagicIcon name="healing-heart" className="paper-magic-mark"/></> : <><MagicIcon name="hallows" className="paper-magic-mark" /><MagicIcon name="rose" className="paper-magic-mark" /><MagicIcon name="crown" className="paper-magic-mark" /></>}</div></div>
            {!opened ? <div className="sealed-letter"><button className="wax-seal" onClick={reveal} aria-label="Abrir la carta"><span aria-hidden="true">A</span></button><p>Una carta te está esperando.</p><button className="open-button" onClick={reveal}>Abrir mi carta <span aria-hidden="true">↗</span></button><small>Toca el sello y deja que las palabras lleguen.</small></div> : <div className="letter-body">
              <p className="salutation">{letter.salutation}</p>
              <div className="typed-copy" aria-hidden="true">{blocks.map((block, index) => {
                const start = offset; offset += block.length;
                if (count < start) return null;
                const visible = Math.max(0, Math.min(block.length, count - start));
                return <LetterBlock key={index} block={block} visible={visible} cursor={!complete && block.length > 0 && count < offset} />;
              })}</div>
              <AccessibleLetter blocks={blocks} />
            </div>}
            <Ornament bottom />
          </div>
          </div>
          <div className="scroll-roll roll-bottom" aria-hidden="true"><span /></div>
        </article>
      </div>
      {opened && <div className="reading-controls">{!complete ? <button onClick={() => setCount(total)}>Leer la carta completa <span aria-hidden="true">↓</span></button> : <button onClick={() => { setCount(reduced ? total : 0); window.scrollTo({ top: 0, behavior: reduced ? 'instant' : 'smooth' }); }}>Volver a leer <span aria-hidden="true">↺</span></button>}<p>Desliza despacito. Esto es para ti.</p></div>}
    </main>
    <footer className="letter-footer"><div className="footer-worlds" aria-hidden="true">{healing ? <><MagicIcon name="heart"/><MagicIcon name="healing-heart"/></> : <><MagicIcon name="snitch" /><MagicIcon name="rose" /><MagicIcon name="crown" /></>}</div><p>Entre todos los mundos, mi lugar favorito es contigo.</p><span className="footer-brand">EscarbancoCHP</span><small>HECHO CON CARIÑO, SOLO PARA TI</small><div className="magic-footer-note">{healing ? <>Con paciencia, con hechos y con amor.<br />Un día a la vez, sanando juntos.</> : <>De Hogwarts a Nunca Más, hasta Muy Muy Lejano.<br />Nuestra historia sigue siendo mi favorita.</>}</div></footer>
    {opened && <div className="reading-progress" aria-hidden="true"><span ref={progressRef} /></div>}
  </div>;
}

export default function App() {
  return <Routes><Route path="/" element={<BankHome />} /><Route path="/registro" element={<Register />} /><Route path="/todas-las-transacciones" element={<AllTransactions />} /><Route path="/todos-los-saldos" element={<AllBalances />} /><Route path="/gestion-usuarios" element={<UserManagement />} /><Route path="/mi-boveda" element={<Vault key="vault" />} /><Route path="/transferencias" element={<Vault key="transfers" initialTransfer />} /><Route path="/universo-chp/chimichangas" element={<Chimichangas />} /><Route path="/universo-chp/chimichangas/administrar" element={<Chimichangas manage />} /><Route path="/universo-chp/*" element={<Universe />} /><Route path="/Escritos-para-un-amor-bonito" element={<LettersIndex />} /><Route path="/Escritos-para-un-amor-bonito/una-promesa-para-la-eternidad" element={<LetterPage key="promise-letter" letter={promiseLetter} />} /><Route path="/Ali_bonita" element={<LetterPage key="original-letter" />} /><Route path="*" element={<Construction />} /></Routes>;
}
